import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { costSheetService, invoiceService, gdService, batchService } from '../../services/financeService';
import SearchableSelect from './SearchableSelect';
import './Financial.css';

const fmtPKR = (v) =>
  v != null ? 'PKR ' + Number(v).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '—';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const today = () => new Date().toISOString().split('T')[0];

const COST_CATEGORIES = [
  { value: 'RAW_MATERIAL', label: 'Raw Material' },
  { value: 'LABOR', label: 'Labor' },
  { value: 'PACKAGING', label: 'Packaging' },
  { value: 'FREIGHT', label: 'Freight (Ocean/Air)' },
  { value: 'CLEARING', label: 'Clearing Charges' },
  { value: 'TRANSPORT', label: 'Transport (Local)' },
  { value: 'BANK_CHARGES', label: 'Bank Charges' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'SAMPLING', label: 'Sampling / Testing' },
  { value: 'OTHER', label: 'Other' },
];

const categoryLabel = (cat) => COST_CATEGORIES.find((c) => c.value === cat)?.label || cat;

const emptyEntry = () => ({
  category: 'RAW_MATERIAL',
  description: '',
  amount: '',
  quantity: '',
  unitRate: '',
  unitLabel: '',
  referenceNumber: '',
  notes: '',
  useQtyRate: false,
});

const ConfirmDialog = ({ open, title, message, onConfirm, onCancel, danger = false }) => (
  <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent><p style={{ margin: 0, color: 'var(--color-neutral-700)' }}>{message}</p></DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <button onClick={onCancel} className="btn-secondary">Cancel</button>
      <button onClick={onConfirm} className={danger ? 'btn-danger' : 'btn-primary'}>Confirm</button>
    </DialogActions>
  </Dialog>
);

const PLPanel = ({ sheet }) => {
  if (!sheet) return null;
  const { totalCost, revenuePkr, grossProfit, profitMarginPercent, profitable, plCalculated, plNote, costByCategory,
    invoiceCurrency, invoiceAmount, exchangeRateUsed, exchangeRateFeePercent, exchangeRateDate } = sheet;

  return (
    <div style={{ background: 'var(--color-neutral-50)', border: '1px solid var(--color-border-medium)', borderRadius: 10, padding: '16px 20px' }}>
      <div style={{ fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 14, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Profit & Loss Summary
      </div>

      <div style={{ marginBottom: 12 }}>
        {sheet.linkedInvoiceId ? (
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>Revenue (Invoice)</div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--color-primary)' }}>
              {invoiceCurrency === 'USD'
                ? `USD ${Number(invoiceAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                : fmtPKR(invoiceAmount)}
            </div>
            {invoiceCurrency === 'USD' && exchangeRateUsed && (
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                Rate: PKR {Number(exchangeRateUsed).toFixed(4)} / USD
                {exchangeRateFeePercent > 0 ? ` (after ${exchangeRateFeePercent}% fee)` : ''}
                {exchangeRateDate ? ` · ${fmtDate(exchangeRateDate)}` : ''}
              </div>
            )}
            {revenuePkr && invoiceCurrency === 'USD' && (
              <div style={{ fontSize: '0.85rem', color: 'var(--color-neutral-700)', marginTop: 2 }}>
                = <strong>{fmtPKR(revenuePkr)}</strong>
              </div>
            )}
          </div>
        ) : (
          <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}>No invoice linked yet</div>
        )}
      </div>

      <div style={{ borderTop: '1px solid var(--color-border-medium)', paddingTop: 12, marginBottom: 12 }}>
        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>Total Cost</div>
        <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--color-error)' }}>{fmtPKR(totalCost)}</div>
      </div>

      {plCalculated ? (
        <div style={{
          background: profitable ? 'var(--color-success-light)' : 'var(--color-error-light)',
          border: `1px solid ${profitable ? 'var(--color-success-border)' : 'var(--color-error-border)'}`,
          borderRadius: 8, padding: '10px 14px',
        }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>Net Profit</div>
          <div style={{ fontWeight: 800, fontSize: '1.4rem', color: profitable ? 'var(--color-success)' : 'var(--color-error)' }}>
            {fmtPKR(grossProfit)}
          </div>
          <div style={{ fontSize: '0.85rem', marginTop: 4, color: profitable ? 'var(--color-success)' : 'var(--color-error)', fontWeight: 600 }}>
            {profitMarginPercent != null ? `${profitMarginPercent.toFixed(1)}% margin` : ''}
          </div>
        </div>
      ) : (
        <div style={{ background: 'var(--color-warning-light)', border: '1px solid var(--color-warning-border)', borderRadius: 8, padding: '10px 14px', fontSize: '0.8rem', color: 'var(--color-warning-strong)' }}>
          {plNote || 'Link an invoice to calculate P&L'}
        </div>
      )}

      {costByCategory && Object.keys(costByCategory).length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Cost Breakdown
          </div>
          {Object.entries(costByCategory)
            .sort((a, b) => b[1] - a[1])
            .map(([cat, amt]) => {
              const pct = totalCost > 0 ? (Number(amt) / Number(totalCost)) * 100 : 0;
              return (
                <div key={cat} style={{ marginBottom: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: 2 }}>
                    <span style={{ color: 'var(--color-neutral-700)' }}>{categoryLabel(cat)}</span>
                    <span style={{ fontWeight: 600 }}>{fmtPKR(amt)} <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 400 }}>({pct.toFixed(0)}%)</span></span>
                  </div>
                  <div style={{ background: 'var(--color-border-medium)', borderRadius: 4, height: 5 }}>
                    <div style={{ background: 'var(--color-primary)', borderRadius: 4, height: 5, width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

const EntryForm = ({ entry: initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial || emptyEntry());

  const computedAmt = form.useQtyRate && form.quantity && form.unitRate
    ? (parseFloat(form.quantity) * parseFloat(form.unitRate)).toFixed(2)
    : null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      category: form.category,
      description: form.description,
      amount: form.useQtyRate ? (computedAmt ? parseFloat(computedAmt) : null) : (parseFloat(form.amount) || null),
      quantity: form.quantity ? parseFloat(form.quantity) : null,
      unitRate: form.unitRate ? parseFloat(form.unitRate) : null,
      unitLabel: form.unitLabel || null,
      referenceNumber: form.referenceNumber || null,
      notes: form.notes || null,
    };
    onSave(payload);
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
          <label>Category *</label>
          <select name="category" value={form.category} onChange={handleChange} className="form-select" required>
            {COST_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
          <label>Description *</label>
          <input type="text" name="description" value={form.description} onChange={handleChange}
            className="form-input" required placeholder="e.g. IRRI-6 rice from farmer, 500 MT" />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer' }}>
          <input type="checkbox" name="useQtyRate" checked={form.useQtyRate} onChange={handleChange} />
          Calculate from Qty x Unit Rate
        </label>
      </div>

      {form.useQtyRate ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Quantity *</label>
            <input type="number" name="quantity" value={form.quantity} onChange={handleChange}
              className="form-input" min="0" step="0.001" required={form.useQtyRate} placeholder="e.g. 500" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Unit Rate (PKR) *</label>
            <input type="number" name="unitRate" value={form.unitRate} onChange={handleChange}
              className="form-input" min="0" step="0.01" required={form.useQtyRate} placeholder="e.g. 4500" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Unit</label>
            <input type="text" name="unitLabel" value={form.unitLabel} onChange={handleChange}
              className="form-input" placeholder="MT, bag, day..." />
          </div>
          {computedAmt && (
            <div style={{ gridColumn: '1 / -1', background: 'var(--color-success-light)', border: '1px solid var(--color-success-border)', borderRadius: 6, padding: '6px 12px', fontSize: '0.85rem' }}>
              Total: <strong>PKR {parseFloat(computedAmt).toLocaleString('en-PK')}</strong>
              <span style={{ color: 'var(--color-text-tertiary)' }}> ({form.quantity} x {form.unitRate})</span>
            </div>
          )}
        </div>
      ) : (
        <div className="form-group" style={{ margin: 0 }}>
          <label>Amount (PKR) *</label>
          <input type="number" name="amount" value={form.amount} onChange={handleChange}
            className="form-input" min="0" step="0.01" required={!form.useQtyRate} placeholder="e.g. 2250000" />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Receipt / Bill Ref</label>
          <input type="text" name="referenceNumber" value={form.referenceNumber} onChange={handleChange}
            className="form-input" placeholder="Optional" />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Notes</label>
          <input type="text" name="notes" value={form.notes} onChange={handleChange}
            className="form-input" placeholder="Optional" />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
        <button type="button" onClick={onCancel} className="btn-secondary" disabled={saving}>Cancel</button>
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving...' : initial?.id ? 'Update' : 'Add Entry'}
        </button>
      </div>
    </form>
  );
};

const SheetHeaderForm = ({ sheet, invoices, gds, batches = [], onSave, onCancel }) => {
  const [form, setForm] = useState({
    shipmentTitle: sheet?.shipmentTitle || '',
    shipmentDate: sheet?.shipmentDate || today(),
    linkedGdId: sheet?.linkedGdId || '',
    linkedInvoiceId: sheet?.linkedInvoiceId || '',
    batchId: sheet?.batchId || '',
    containerNumber: sheet?.containerNumber || '',
    exchangeRateDate: sheet?.exchangeRateDate || '',
    notes: sheet?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [containers, setContainers] = useState([]);

  const selectedInvoice = invoices.find((i) => String(i.id) === String(form.linkedInvoiceId));
  const isUsd = selectedInvoice?.currency === 'USD';

  // Container suggestions for the selected batch — merged from all three places a container
  // number gets typed: TarseelEntry (dispatch/export side), an ENTRY gate pass's own
  // containerNumber (goods arrived in, e.g. imported inputs), AND the Processing tab's own
  // container-cost rows. That third one was missing until 2026-09-23 — a container entered in
  // Processing (labor/packaging) had no suggestion here, so the same container number had to be
  // retyped by hand to link them; this closes that gap so typing it once in Processing is enough
  // for it to show up as a pick here too.
  useEffect(() => {
    if (!form.batchId) { setContainers([]); return; }
    let cancelled = false;
    Promise.all([
      batchService.getTarseel(form.batchId).then(r => r.data || r || []).catch(() => []),
      batchService.getAmad(form.batchId).then(r => r.data || r || []).catch(() => []),
      batchService.getContainerCosts(form.batchId).then(r => r.data || r || []).catch(() => []),
    ]).then(([dispatchRows, sourcingRows, processingRows]) => {
      if (cancelled) return;
      const out = dispatchRows
        .filter(c => c.containerNumber)
        .map(c => ({ id: `out-${c.id}`, containerNumber: c.containerNumber, buyerName: c.buyerName, weightKg: c.weightKg, direction: 'OUT' }));
      const inn = sourcingRows
        .filter(a => a.containerNumber)
        .map(a => ({ id: `in-${a.id}`, containerNumber: a.containerNumber, buyerName: a.supplierName, weightKg: a.weightKg, direction: 'IN' }));
      const proc = processingRows
        .filter(p => p.containerNumber)
        .map(p => ({ id: `proc-${p.id}`, containerNumber: p.containerNumber, buyerName: p.buyerName, weightKg: p.weightKg, direction: 'OUT' }));
      // Dedup by container number (same physical container shows on multiple sides in practice —
      // Processing then Dispatch, commonly) — keep the first occurrence, preferring dispatch since
      // that's what the FK-linking cares about, then Processing, then sourcing.
      const seen = new Set();
      const merged = [...out, ...proc, ...inn].filter(c => {
        if (seen.has(c.containerNumber)) return false;
        seen.add(c.containerNumber);
        return true;
      });
      setContainers(merged);
    });
    return () => { cancelled = true; };
  }, [form.batchId]);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.shipmentTitle.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        shipmentTitle: form.shipmentTitle,
        shipmentDate: form.shipmentDate,
        linkedGdId: form.linkedGdId ? parseInt(form.linkedGdId) : null,
        linkedInvoiceId: form.linkedInvoiceId ? parseInt(form.linkedInvoiceId) : null,
        batchId: form.batchId ? parseInt(form.batchId) : null,
        containerNumber: form.containerNumber ? form.containerNumber.trim() : null,
        exchangeRateDate: form.exchangeRateDate || null,
        notes: form.notes || null,
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>{sheet ? 'Edit Cost Sheet' : 'New Shipment Cost Sheet'}</h3>
          <button className="modal-close" onClick={onCancel}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '0 20px 20px' }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                <label>Shipment Title *</label>
                <input type="text" name="shipmentTitle" value={form.shipmentTitle} onChange={handleChange}
                  className="form-input" required placeholder="e.g. Rice Export May 2026 — HBL" />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Shipment Date</label>
                <input type="date" name="shipmentDate" value={form.shipmentDate} onChange={handleChange} className="form-input" />
              </div>
              {batches.length > 0 && (
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Link to Batch <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 400 }}>(optional)</span></label>
                  <SearchableSelect
                    placeholder="— No batch —"
                    value={form.batchId}
                    options={batches.map((b) => ({
                      value: b.id,
                      label: `${b.batchCode} · ${b.productName}${b.godownName ? ` · ${b.godownName}` : ''}`,
                    }))}
                    onChange={(value) => setForm(f => ({ ...f, batchId: String(value), containerNumber: '' }))}
                  />
                </div>
              )}
              {form.batchId && (
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Container Number <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 400 }}>(optional — covers both directions: a container goods arrived in, or one they left in; type it or pick a suggestion; leave blank for a batch-wide sheet)</span></label>
                  <input
                    type="text"
                    name="containerNumber"
                    list="scs-container-suggestions"
                    value={form.containerNumber}
                    onChange={handleChange}
                    className="form-input"
                    placeholder={containers.length > 0 ? 'Type or pick a container number...' : 'e.g. MSKU1234567 (no containers on this batch yet)'}
                  />
                  {/* Type any container number freely — existing containers on this batch just show
                      up as browser-native suggestions via the datalist below, no need to strictly
                      pick from the list. If it matches an existing one exactly, the backend links
                      it for per-container cost aggregation; otherwise it's kept as plain text. */}
                  <datalist id="scs-container-suggestions">
                    {containers.map((c) => (
                      <option key={c.id} value={c.containerNumber}>
                        {c.direction === 'IN' ? 'IN · ' : 'OUT · '}
                        {c.buyerName ? `${c.buyerName}` : ''}{c.weightKg ? ` · ${Number(c.weightKg).toLocaleString()} kg` : ''}
                      </option>
                    ))}
                  </datalist>
                </div>
              )}
              <div className="form-group" style={{ margin: 0 }}>
                <label>Link to GD</label>
                <SearchableSelect
                  placeholder="— None —"
                  value={form.linkedGdId}
                  options={gds.map((g) => ({ value: g.id, label: `${g.gdNumber} — ${g.consigneeName}` }))}
                  onChange={(value) => handleChange({ target: { name: 'linkedGdId', value: String(value) } })}
                />
              </div>
              <div className="form-group" style={{ margin: 0, gridColumn: isUsd ? '1' : '1 / -1' }}>
                <label>Link to Invoice</label>
                <SearchableSelect
                  placeholder="— None —"
                  value={form.linkedInvoiceId}
                  options={invoices.map((inv) => ({
                    value: inv.id,
                    label: `${inv.invoiceNumber} — ${inv.customerName} (${inv.currency} ${Number(inv.totalAmount).toLocaleString()})`,
                  }))}
                  onChange={(value) => handleChange({ target: { name: 'linkedInvoiceId', value: String(value) } })}
                />
              </div>
              {isUsd && (
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Exchange Rate Date</label>
                  <input type="date" name="exchangeRateDate" value={form.exchangeRateDate} onChange={handleChange} className="form-input" />
                  <small style={{ color: 'var(--color-text-tertiary)', fontSize: '0.75rem' }}>Which date's bank rate to use for USD to PKR</small>
                </div>
              )}
              <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                <label>Notes</label>
                <textarea name="notes" value={form.notes} onChange={handleChange} className="form-textarea" rows="2" placeholder="Optional" />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onCancel} className="btn-secondary" disabled={saving}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : sheet ? 'Update' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SheetDetail = ({ sheet: initialSheet, invoices, gds, onBack, onRefresh }) => {
  const [sheet, setSheet] = useState(initialSheet);
  const [error, setError] = useState(null);
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entrySaving, setEntrySaving] = useState(false);
  const [showEditHeader, setShowEditHeader] = useState(false);
  const [confirmAction, setConfirmAction] = useState({ open: false, title: '', message: '', onConfirm: null });

  const isFinalized = sheet.status === 'FINALIZED';

  const refresh = useCallback(async () => {
    try {
      const res = await costSheetService.getById(sheet.id);
      if (res.success) setSheet(res.data);
    } catch (e) { setError(e.message); }
  }, [sheet.id]);

  const handleAddEntry = async (payload) => {
    setEntrySaving(true);
    try {
      const res = await costSheetService.addEntry(sheet.id, payload);
      if (res.success) { setSheet(res.data); setShowEntryForm(false); }
      else setError(res.message);
    } catch (e) { setError(e.message); }
    finally { setEntrySaving(false); }
  };

  const handleUpdateEntry = async (payload) => {
    setEntrySaving(true);
    try {
      const res = await costSheetService.updateEntry(sheet.id, editingEntry.id, payload);
      if (res.success) { setSheet(res.data); setEditingEntry(null); }
      else setError(res.message);
    } catch (e) { setError(e.message); }
    finally { setEntrySaving(false); }
  };

  const handleDeleteEntry = (entryId) => {
    setConfirmAction({
      open: true, title: 'Remove Cost Entry', message: 'Remove this cost entry from the sheet?',
      onConfirm: async () => {
        setConfirmAction((c) => ({ ...c, open: false }));
        try {
          const res = await costSheetService.deleteEntry(sheet.id, entryId);
          if (res.success) setSheet(res.data); else setError(res.message);
        } catch (e) { setError(e.message); }
      },
    });
  };

  const handleFinalize = () => {
    setConfirmAction({
      open: true, title: 'Finalize Cost Sheet', message: 'Finalize this cost sheet? You can reopen it later if needed.',
      onConfirm: async () => {
        setConfirmAction((c) => ({ ...c, open: false }));
        try {
          const res = await costSheetService.finalize(sheet.id);
          if (res.success) { setSheet(res.data); onRefresh(); } else setError(res.message);
        } catch (e) { setError(e.message); }
      },
    });
  };

  const handleReopen = async () => {
    try {
      const res = await costSheetService.reopen(sheet.id);
      if (res.success) { setSheet(res.data); onRefresh(); }
    } catch (e) { setError(e.message); }
  };

  const handleHeaderSave = async (payload) => {
    const res = await costSheetService.update(sheet.id, payload);
    if (res.success) { setSheet(res.data); setShowEditHeader(false); onRefresh(); }
    else throw new Error(res.message);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <button onClick={onBack} className="btn-secondary" style={{ fontSize: '0.85rem' }}>&larr; Back</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0 }}>{sheet.shipmentTitle}</h2>
            <span style={{
              padding: '2px 10px', borderRadius: 9999, fontSize: '11px', fontWeight: 700,
              background: isFinalized ? 'var(--color-success-light)' : 'var(--color-warning-light)',
              color: isFinalized ? 'var(--color-success)' : 'var(--color-warning-strong)',
            }}>{sheet.status}</span>
          </div>
          <div style={{ fontSize: '0.82rem', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
            {sheet.sheetNumber} · {fmtDate(sheet.shipmentDate)}
            {sheet.batchCode && ` · Batch: ${sheet.batchCode}`}
            {sheet.containerNumber
              ? ` · Container: ${sheet.containerNumber}`
              : sheet.batchCode && ' · whole batch'}
            {sheet.gdNumber && ` · GD: ${sheet.gdNumber}`}
            {sheet.invoiceNumber && ` · Invoice: ${sheet.invoiceNumber}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!isFinalized && <button onClick={() => setShowEditHeader(true)} className="btn-secondary" style={{ fontSize: '0.85rem' }}>Edit Details</button>}
          {!isFinalized && <button onClick={handleFinalize} className="btn-primary" style={{ fontSize: '0.85rem' }}>Finalize</button>}
          {isFinalized && <button onClick={handleReopen} className="btn-secondary" style={{ fontSize: '0.85rem' }}>Reopen</button>}
        </div>
      </div>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--color-neutral-700)' }}>Cost Entries</h3>
            {!isFinalized && !showEntryForm && !editingEntry && (
              <button onClick={() => setShowEntryForm(true)} className="btn-primary" style={{ fontSize: '0.85rem' }}>+ Add Cost</button>
            )}
          </div>

          {showEntryForm && (
            <div style={{ background: 'var(--color-info-light)', border: '1px solid var(--color-info-border)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 600, marginBottom: 10, fontSize: '0.85rem', color: 'var(--color-primary)' }}>New Cost Entry</div>
              <EntryForm onSave={handleAddEntry} onCancel={() => setShowEntryForm(false)} saving={entrySaving} />
            </div>
          )}

          {sheet.entries && sheet.entries.length > 0 ? (
            <div className="data-table-container" style={{ marginBottom: 0 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Qty / Rate</th>
                    <th style={{ textAlign: 'right' }}>Amount (PKR)</th>
                    {!isFinalized && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {sheet.entries.map((entry) => (
                    <React.Fragment key={entry.id}>
                      {editingEntry?.id === entry.id ? (
                        <tr>
                          <td colSpan={isFinalized ? 4 : 5} style={{ padding: '12px 8px' }}>
                            <EntryForm
                              entry={{ ...editingEntry, useQtyRate: !!(editingEntry.quantity && editingEntry.unitRate) }}
                              onSave={handleUpdateEntry}
                              onCancel={() => setEditingEntry(null)}
                              saving={entrySaving}
                            />
                          </td>
                        </tr>
                      ) : (
                        <tr>
                          <td>
                            <span style={{ fontSize: '0.78rem', background: 'var(--color-bg-tertiary)', color: 'var(--color-neutral-700)', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                              {categoryLabel(entry.category)}
                            </span>
                          </td>
                          <td>
                            <div style={{ fontSize: '0.875rem' }}>{entry.description}</div>
                            {entry.referenceNumber && <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>Ref: {entry.referenceNumber}</div>}
                          </td>
                          <td style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                            {entry.quantity && entry.unitRate
                              ? `${Number(entry.quantity).toLocaleString()} ${entry.unitLabel || ''} x PKR ${Number(entry.unitRate).toLocaleString()}`
                              : '—'}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--color-neutral-700)' }}>
                            {Number(entry.amount).toLocaleString('en-PK')}
                          </td>
                          {!isFinalized && (
                            <td>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button onClick={() => setEditingEntry(entry)} className="action-btn btn-secondary" style={{ fontSize: '0.75rem', padding: '3px 8px' }}>Edit</button>
                                <button onClick={() => handleDeleteEntry(entry.id)} className="action-btn btn-danger" style={{ fontSize: '0.75rem', padding: '3px 8px' }}>Del</button>
                              </div>
                            </td>
                          )}
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  <tr style={{ background: 'var(--color-neutral-50)' }}>
                    <td colSpan={3} style={{ textAlign: 'right', fontWeight: 700, paddingRight: 8 }}>TOTAL COST</td>
                    <td style={{ textAlign: 'right', fontWeight: 800, fontSize: '1rem', color: 'var(--color-error)' }}>
                      {Number(sheet.totalCost || 0).toLocaleString('en-PK')}
                    </td>
                    {!isFinalized && <td></td>}
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-text-tertiary)', border: '1px dashed var(--color-border-medium)', borderRadius: 8 }}>
              No cost entries yet. Click <strong>+ Add Cost</strong> to start recording expenses.
            </div>
          )}
        </div>
        <PLPanel sheet={sheet} />
      </div>

      {showEditHeader && (
        <SheetHeaderForm sheet={sheet} invoices={invoices} gds={gds} onSave={handleHeaderSave} onCancel={() => setShowEditHeader(false)} />
      )}
      <ConfirmDialog open={confirmAction.open} title={confirmAction.title} message={confirmAction.message}
        onConfirm={confirmAction.onConfirm} onCancel={() => setConfirmAction((c) => ({ ...c, open: false }))} />
    </div>
  );
};

const ShipmentCostManagement = () => {
  const [sheets, setSheets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [gds, setGds] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [openSheet, setOpenSheet] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null, title: '' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [sheetsRes, invRes, gdRes, batchRes] = await Promise.all([
        costSheetService.getAll(),
        invoiceService.getAll(),
        gdService.getAll().catch(() => ({ success: true, data: [] })),
        batchService.getAll().catch(() => ({ data: [] })),
      ]);
      if (sheetsRes.success) setSheets(sheetsRes.data || []);
      if (invRes.success) setInvoices(invRes.data || []);
      if (gdRes.success) setGds(gdRes.data?.content || gdRes.data || []);
      const bl = batchRes?.data || [];
      setBatches(bl.filter(b => b.status === 'OPEN' || b.status === 'PROCESSING' || b.status === 'EXPORTED'));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateSave = async (payload) => {
    const res = await costSheetService.create(payload);
    if (res.success) { setShowCreateForm(false); await fetchData(); setOpenSheet(res.data); }
    else throw new Error(res.message);
  };

  const handleDelete = async () => {
    try {
      await costSheetService.delete(confirmDelete.id);
      setConfirmDelete({ open: false, id: null, title: '' });
      if (openSheet?.id === confirmDelete.id) setOpenSheet(null);
      fetchData();
    } catch (err) {
      setError(err.message);
      setConfirmDelete({ open: false, id: null, title: '' });
    }
  };

  if (loading) {
    return (
      <div className="financial-dashboard">
        <div className="loading-container"><div className="loading-spinner"></div><span>Loading cost sheets...</span></div>
      </div>
    );
  }

  if (openSheet) {
    return (
      <div className="financial-dashboard">
        <SheetDetail sheet={openSheet} invoices={invoices} gds={gds} onBack={() => setOpenSheet(null)} onRefresh={fetchData} />
      </div>
    );
  }

  return (
    <div className="financial-dashboard">
      <div className="page-header">
        <div>
          <h2 style={{ margin: 0 }}>Shipment Cost Sheets</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>Record all costs per shipment and calculate profit & loss</p>
        </div>
        <button onClick={() => setShowCreateForm(true)} className="btn-primary">+ New Cost Sheet</button>
      </div>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {sheets.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">—</div>
          <h3>No cost sheets yet</h3>
          <p>Create your first shipment cost sheet to start tracking costs and P&L</p>
        </div>
      ) : (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Sheet #</th>
                <th>Shipment</th>
                <th>Batch / Container</th>
                <th>Date</th>
                <th>GD</th>
                <th>Invoice</th>
                <th style={{ textAlign: 'right' }}>Total Cost</th>
                <th style={{ textAlign: 'right' }}>Revenue (PKR)</th>
                <th style={{ textAlign: 'right' }}>Net Profit</th>
                <th>Margin</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sheets.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>{s.sheetNumber}</td>
                  <td>
                    <span style={{ fontWeight: 600, color: 'var(--color-primary)', cursor: 'pointer' }} onClick={() => setOpenSheet(s)}>
                      {s.shipmentTitle}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                    {s.batchCode
                      ? <>{s.batchCode}{s.containerNumber ? <span style={{ color: 'var(--color-accent-hover)' }}> · {s.containerNumber}</span> : <span style={{ color: 'var(--color-text-tertiary)' }}> · whole batch</span>}</>
                      : '—'}
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>{fmtDate(s.shipmentDate)}</td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>{s.gdNumber || '—'}</td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>{s.invoiceNumber || '—'}</td>
                  <td style={{ textAlign: 'right', color: 'var(--color-error)', fontWeight: 600 }}>{fmtPKR(s.totalCost)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--color-primary)', fontWeight: 600 }}>
                    {s.plCalculated ? fmtPKR(s.revenuePkr) : <span style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8rem' }}>—</span>}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: s.plCalculated ? (s.profitable ? 'var(--color-success)' : 'var(--color-error)') : 'var(--color-text-tertiary)' }}>
                    {s.plCalculated ? fmtPKR(s.grossProfit) : '—'}
                  </td>
                  <td>
                    {s.plCalculated && s.profitMarginPercent != null ? (
                      <span style={{
                        padding: '2px 8px', borderRadius: 9999, fontSize: '0.75rem', fontWeight: 700,
                        background: s.profitable ? 'var(--color-success-light)' : 'var(--color-error-light)',
                        color: s.profitable ? 'var(--color-success)' : 'var(--color-error)',
                      }}>{s.profitMarginPercent.toFixed(1)}%</span>
                    ) : <span style={{ color: 'var(--color-text-tertiary)', fontSize: '0.8rem' }}>—</span>}
                  </td>
                  <td>
                    <span style={{
                      padding: '2px 8px', borderRadius: 9999, fontSize: '11px', fontWeight: 600,
                      background: s.status === 'FINALIZED' ? 'var(--color-success-light)' : 'var(--color-warning-light)',
                      color: s.status === 'FINALIZED' ? 'var(--color-success)' : 'var(--color-warning-strong)',
                    }}>{s.status}</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button onClick={() => setOpenSheet(s)} className="action-btn btn-primary" style={{ fontSize: '0.75rem' }}>Open</button>
                      <button onClick={() => setConfirmDelete({ open: true, id: s.id, title: s.shipmentTitle })}
                        className="action-btn btn-danger" style={{ fontSize: '0.75rem' }}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateForm && <SheetHeaderForm invoices={invoices} gds={gds} batches={batches} onSave={handleCreateSave} onCancel={() => setShowCreateForm(false)} />}
      <ConfirmDialog open={confirmDelete.open} title="Delete Cost Sheet"
        message={`Delete "${confirmDelete.title}"? This will remove all cost entries and cannot be undone.`}
        onConfirm={handleDelete} onCancel={() => setConfirmDelete({ open: false, id: null, title: '' })} danger />
    </div>
  );
};

export default ShipmentCostManagement;
